<template>
  <div class="modern-master-view pb-12">
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm">
          <v-icon color="white">fas fa-industry</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Rentabilidad OT</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">
            Facturado CxC vs precio comercial vs costo post-costeo
          </div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn outlined color="primary" class="font-weight-black text-uppercase tracking-widest rounded-lg px-6" :loading="loading" @click="fetchAll">
        <v-icon left small>fas fa-redo</v-icon> Actualizar
      </v-btn>
    </v-row>

    <v-card outlined class="modern-card pa-6 mb-6 mx-4">
      <v-row dense align="center">
        <v-col cols="12" md="3">
          <v-text-field v-model="start" type="date" outlined dense hide-details label="Desde" class="modern-input" @change="fetchAll" />
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field v-model="end" type="date" outlined dense hide-details label="Hasta" class="modern-input" @change="fetchAll" />
        </v-col>
      </v-row>
    </v-card>

    <v-row class="mb-6 px-4">
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">OT cerradas</div>
          <div class="text-h5 font-weight-black">{{ kpis.ot_count || 0 }}</div>
          <div class="caption grey--text">Con ingreso {{ kpis.with_revenue_count || 0 }} · facturadas {{ kpis.with_billed_count || 0 }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Ingreso</div>
          <div class="text-h5 font-weight-black primary--text">{{ kpis.revenue_total | moneda }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Costo</div>
          <div class="text-h5 font-weight-black">{{ kpis.cost_total | moneda }}</div>
          <div class="caption grey--text">Fact. compra {{ kpis.purchase_invoiced_total | moneda }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Margen bruto</div>
          <div class="text-h5 font-weight-black" :class="(kpis.gross_margin || 0) >= 0 ? 'success--text' : 'error--text'">
            {{ kpis.gross_margin | moneda }}
          </div>
          <div class="caption grey--text">{{ formatPct(kpis.gross_margin_pct) }} · +{{ kpis.profitable_count || 0 }} / −{{ kpis.loss_count || 0 }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-card outlined class="modern-card mx-4">
      <v-data-table :headers="headers" :items="items" :loading="loading" :items-per-page="50" class="elevation-0" no-data-text="No hay OT cerradas en el período.">
        <template v-slot:item.revenue="{ item }">{{ item.revenue | moneda }}</template>
        <template v-slot:item.revenue_billed="{ item }">{{ item.revenue_billed | moneda }}</template>
        <template v-slot:item.revenue_commercial="{ item }">{{ item.revenue_commercial | moneda }}</template>
        <template v-slot:item.cost_actual="{ item }">{{ item.cost_actual | moneda }}</template>
        <template v-slot:item.purchase_invoiced="{ item }">
          <div>{{ item.purchase_invoiced | moneda }}</div>
          <div v-if="item.purchase_invoice_count" class="caption grey--text">{{ item.purchase_invoice_count }} doc.</div>
        </template>
        <template v-slot:item.gross_margin="{ item }">
          <span :class="(item.gross_margin || 0) >= 0 ? 'success--text' : 'error--text'" class="font-weight-black">
            {{ item.gross_margin | moneda }}
          </span>
        </template>
        <template v-slot:item.gross_margin_pct="{ item }">{{ formatPct(item.gross_margin_pct) }}</template>
        <template v-slot:item.revenue_source="{ item }">
          <v-chip x-small>{{ sourceLabel(item.revenue_source) }}</v-chip>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import notifications from '@/utils/notifications'
import { fetchOtProfitability } from '@/api/finance'

export default {
  name: 'FinanceOtProfitability',
  data() {
    const now = new Date()
    const start = new Date(now.getFullYear(), now.getMonth(), 1)
    return {
      loading: false,
      start: start.toISOString().slice(0, 10),
      end: now.toISOString().slice(0, 10),
      kpis: {},
      items: [],
      headers: [
        { text: 'OT', value: 'number' },
        { text: 'Cierre', value: 'completed_at' },
        { text: 'Cliente', value: 'client_name' },
        { text: 'Ingreso', value: 'revenue', align: 'end' },
        { text: 'Facturado', value: 'revenue_billed', align: 'end' },
        { text: 'Comercial', value: 'revenue_commercial', align: 'end' },
        { text: 'Costo', value: 'cost_actual', align: 'end' },
        { text: 'Fact. compra', value: 'purchase_invoiced', align: 'end' },
        { text: 'Margen', value: 'gross_margin', align: 'end' },
        { text: '%', value: 'gross_margin_pct', align: 'end' },
        { text: 'Fuente', value: 'revenue_source' },
      ],
    }
  },
  methods: {
    formatPct(value) {
      if (value === null || value === undefined) return '—'
      return `${Number(value).toFixed(1)}%`
    },
    sourceLabel(source) {
      if (source === 'ar_billed') return 'Facturado'
      if (source === 'commercial') return 'Comercial'
      return 'Sin ingreso'
    },
    async fetchAll() {
      this.loading = true
      try {
        const data = await fetchOtProfitability({ start: this.start, end: this.end })
        this.kpis = data.kpis || {}
        this.items = data.items || []
      } catch (e) {
        notifications.showError('No se pudo cargar la rentabilidad de OT')
      } finally {
        this.loading = false
      }
    },
  },
  created() {
    this.fetchAll()
  },
}
</script>
